import {Presenter} from '../../Support/with_presenter';
import {PreviewWorkout} from '../../Models/PreviewWorkout';
import {WorkoutsService} from '../../Network/WorkoutsService';
import {
    errorResponse,
    loadingResponse,
    NetworkRequestStatus,
    NetworkResult,
    successResponse,
} from '../../Network/NetworkResult';
import {StrokesService} from '../../Network/StrokesService';
import {DownloadedIndoorWorkout} from '../../Models/DownloadedIndoorWorkout';
import {FullWorkout} from '../../Models/FullWorkout';
import {MeService} from '../../Network/MeService';
import {Me} from '../../Models/Me';
import {workoutTitleFormatter} from '../Support/workoutTitleFormatter';
import {WorkoutExecutionType} from '../../Models/WorkoutExecutionType';
import {GeoTrack} from '../../Models/GeoTrack';
import {TracksDownloadService} from '../../Network/TracksDownloadService';
import {HeartRateTrack} from '../../Models/HeartRateTrack';
import {GeoTrackStrokes} from '../../Models/GeoTrackStrokes';

export class WorkoutShowPresenter implements Presenter {
    public me: NetworkResult<Me> = loadingResponse();
    public fullWorkoutResult: NetworkResult<FullWorkout> = loadingResponse();
    public downloadedIndoorWorkoutResult: NetworkResult<DownloadedIndoorWorkout> = loadingResponse();

    public get tracksDownloadResult(): NetworkResult<{
        geo: GeoTrack;
        geoStrokes: GeoTrackStrokes | null;
        heartRate: HeartRateTrack | null;
    }> {
        const geoTrackDownloadResult = this.geoTrackDownloadResult;
        if (
            geoTrackDownloadResult.status === NetworkRequestStatus.Loading ||
            this.heartRateTrackDownloadResult?.status === NetworkRequestStatus.Loading ||
            this.geoStrokesTrackDownloadResult?.status === NetworkRequestStatus.Loading
        ) {
            return loadingResponse();
        }
        if (
            geoTrackDownloadResult.status === NetworkRequestStatus.Error ||
            this.heartRateTrackDownloadResult?.status === NetworkRequestStatus.Error ||
            this.geoStrokesTrackDownloadResult?.status === NetworkRequestStatus.Error
        ) {
            return errorResponse();
        }

        return successResponse({
            geo: geoTrackDownloadResult.data,
            geoStrokes: this.geoStrokesTrackDownloadResult?.data ?? null,
            heartRate: this.heartRateTrackDownloadResult?.data ?? null,
        });
    }

    private geoTrackDownloadResult: NetworkResult<GeoTrack> = loadingResponse();
    private geoStrokesTrackDownloadResult: null | NetworkResult<GeoTrackStrokes> = null;
    private heartRateTrackDownloadResult: null | NetworkResult<HeartRateTrack> = null;

    public get title(): string | null {
        if (this.previewWorkout) {
            return workoutTitleFormatter(this.previewWorkout);
        }
        if (this.fullWorkoutResult.status === NetworkRequestStatus.Success) {
            return workoutTitleFormatter(this.fullWorkoutResult.data);
        }

        return null;
    }

    constructor(
        public id: number,
        public previewWorkout: PreviewWorkout | null,
        private meService: MeService,
        private workoutService: WorkoutsService,
        private strokesService: StrokesService,
        private tracksDownloadService: TracksDownloadService,
    ) {}

    public async mount(): Promise<void> {
        this.me = await this.meService.get();
        this.fullWorkoutResult = await this.workoutService.full(this.id);

        if (this.fullWorkoutResult.status === NetworkRequestStatus.Success) {
            const data = this.fullWorkoutResult.data;
            if (data.execution_type === WorkoutExecutionType.INDOOR) {
                this.downloadedIndoorWorkoutResult = await this.strokesService.forWorkoutId(this.id);
            }

            if (data.execution_type === WorkoutExecutionType.OUTDOOR) {
                const promises = [];

                promises.push(
                    (async () => {
                        this.geoTrackDownloadResult = await this.tracksDownloadService.forGeoTrackUrl(
                            data.track_geo_url,
                        );
                    })(),
                );

                if (data.track_geo_strokes_url) {
                    this.geoStrokesTrackDownloadResult = loadingResponse();
                    promises.push(
                        (async () => {
                            this.geoStrokesTrackDownloadResult = await this.tracksDownloadService.forGeoStrokesTrackUrl(
                                data.track_geo_strokes_url,
                            );
                        })(),
                    );
                }

                if (data.track_heart_rate_url) {
                    this.heartRateTrackDownloadResult = loadingResponse();
                    promises.push(
                        (async () => {
                            this.heartRateTrackDownloadResult = await this.tracksDownloadService.forHeartRateTrackUrl(
                                data.track_heart_rate_url,
                            );
                        })(),
                    );
                }

                await Promise.all(promises);
            }
        }
    }

    public unmount(): void {
        /* Noop */
    }
}
